<template>
  <section id="projects" class="projects">
    <h2>{{ $t("projects.title") }}</h2>
    <div class="projects-grid">
      <div
        v-for="project in projects"
        :key="project.title"
        class="project-card"
      >
        <div
          class="image-wrapper"
          :class="{ 'logo-container': project.isLogo }"
        >
          <img
            :src="project.image"
            :alt="project.title"
            :class="{ 'logo-image': project.isLogo }"
            loading="lazy"
          />
          <span class="badge" :class="project.status">{{
            $t(`projects.${project.status}`)
          }}</span>
        </div>
        <div class="card-content">
          <h3>{{ project.title }}</h3>
          <p>{{ project.description }}</p>
          <div class="tech-list">
            <div
              v-for="tech in project.technologies"
              :key="tech.name"
              class="tech-badge"
              :aria-label="tech.name"
              role="listitem"
            >
              <font-awesome-icon
                v-if="tech.icon"
                :icon="tech.icon"
                class="tech-icon"
              />
              <component
                v-else-if="tech.iconComponent"
                :is="tech.iconComponent"
                :isDarkMode="isDarkMode"
                class="tech-icon"
              />
              <span>{{ tech.name }}</span>
            </div>
          </div>
          <div class="buttons" role="group" aria-label="Enlaces del proyecto">
            <a
              v-if="project.demo"
              :href="project.demo"
              target="_blank"
              class="btn demo-btn"
              aria-label="Demo"
              rel="noopener noreferrer"
            >
              <font-awesome-icon :icon="['fas', 'eye']" class="btn-icon" />
            </a>
            <a
              v-if="project.repo"
              :href="project.repo"
              target="_blank"
              class="btn repo-btn"
              aria-label="Repositorio"
              rel="noopener noreferrer"
            >
              <font-awesome-icon :icon="['fab', 'github']" class="btn-icon" />
            </a>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import logoHomi from "@/assets/images/homi.png";
import logoPortfolio from "@/assets/images/portfolio.png";
import logoWattsBradley from "@/assets/images/wattsbradley.png";
import logoVirgenGracia from "@/assets/images/virgengracia.png";
import MySQLIcon from "@/components/icons/MySQLIcon.vue";
import NestJSIcon from "@/components/icons/NestJSIcon.vue";
import PrismaIcon from "@/components/icons/PrismaIcon.vue";
import SassIcon from "@/components/icons/SassIcon.vue";
export default {
  name: "ProjectsSection",
  components: {
    MySQLIcon,
    NestJSIcon,
    PrismaIcon,
    SassIcon,
  },
  data() {
    return {
      projectList: [
        {
          title: "Homi",
          image: logoHomi,
          technologies: [
            { name: "Angular", icon: ["fab", "angular"] },
            { name: "NestJS", iconComponent: "NestJSIcon" },
            { name: "MySQL", iconComponent: "MySQLIcon" },
            { name: "Prisma", iconComponent: "PrismaIcon" },
            { name: "Docker", icon: ["fab", "docker"] },
          ],
          status: "inProgress",
          demo: null,
          repo: null,
          isLogo: false,
          key: "homi",
        },
        {
          title: "Watts & Bradley",
          image: logoWattsBradley,
          technologies: [
            { name: "Angular", icon: ["fab", "angular"] },
            { name: "SCSS", iconComponent: "SassIcon" },
            { name: "Node.js", icon: ["fab", "node-js"] },
            { name: "MySQL", iconComponent: "MySQLIcon" },
          ],
          status: "inProgress",
          demo: null,
          repo: null,
          isLogo: false,
          key: "wattsBradley",
        },
        {
          title: "Gestión Virgen de Gracia",
          image: logoVirgenGracia,
          technologies: [
            { name: "Angular", icon: ["fab", "angular"] },
            { name: "Node.js", icon: ["fab", "node-js"] },
            { name: "MySQL", iconComponent: "MySQLIcon" },
            { name: "Font Awesome", icon: ["fas", "font-awesome"] },
          ],
          status: "done",
          demo: null,
          repo: null,
          isLogo: false,
          key: "gestionVdg",
        },
        {
          title: "Portafolio",
          image: logoPortfolio,
          technologies: [
            { name: "Vue.js", icon: ["fab", "vuejs"] },
            { name: "Node.js", icon: ["fab", "node-js"] },
            { name: "MySQL", iconComponent: "MySQLIcon" },
            { name: "Font Awesome", icon: ["fas", "font-awesome"] },
          ],
          status: "done",
          demo: null,
          repo: null,
          isLogo: false,
          key: "portfolio",
        },
      ],
    };
  },
  computed: {
    projects() {
      return this.projectList.map((project) => ({
        ...project,
        description: this.$t(`projects.${project.key}`),
      }));
    },
    isDarkMode() {
      return this.$store.getters.isDarkMode;
    },
  },
};
</script>

<style scoped>
.projects {
  padding: 60px 20px;
  max-width: 1200px;
  margin: auto;
  text-align: center;
}

.projects h2 {
  margin-bottom: 40px;
  color: #333;
}

.projects-grid {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 40px;
}

.project-card {
  padding: 20px;
  background: #fff;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.05);
  transition: transform 0.3s ease;
  display: flex;
  flex-direction: column;
  flex: 1 1 320px;
  max-width: 320px;
}

.project-card:hover {
  transform: translateY(-10px);
}

.image-wrapper {
  position: relative;
  aspect-ratio: 16 / 9;
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #f8f8f8;
  border-radius: 12px;
}

/* Contenedor específico para logos */
.logo-container {
  padding: 20px;
}

.image-wrapper img {
  width: 100%;
  height: 100%;
  border-radius: 12px;
  object-position: center;
}

/* Estilo específico para imágenes de logos */
.logo-image {
  object-fit: contain;
  max-height: 80%;
  max-width: 80%;
  width: auto;
  height: auto;
}

.badge {
  position: absolute;
  top: 12px;
  left: 12px;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 0.75rem;
  color: #fff;
  font-weight: bold;
  text-transform: uppercase;
}

.badge.inProgress {
  background-color: #f0ad4e;
}

.badge.done {
  background-color: #5cb85c;
}

.card-content {
  text-align: left;
  flex-grow: 1;
  padding: 20px 0 0 0;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.card-content h3 {
  margin: 0 0 10px;
  font-size: 1.4rem;
  color: #222;
}

.card-content p {
  flex-grow: 1;
  color: #555;
  font-size: 0.95rem;
  margin-bottom: 15px;
}

.tech-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.tech-badge {
  display: flex;
  align-items: center;
  gap: 6px;
  background: #eee;
  border-radius: 12px;
  padding: 5px 10px;
  font-size: 0.8rem;
  color: #333;
}

.tech-icon {
  width: 20px;
  height: 20px;
}

.buttons {
  display: flex;
  gap: 15px;
  justify-content: center;
  margin-top: 20px;
}

.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;
  background-color: #42b983;
  color: white;
  border-radius: 8px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  font-size: 24px; /* Icon size */
  cursor: pointer;
}

.btn:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 12px rgba(0, 0, 0, 0.15);
}

.demo-btn {
  background-color: #4d4d4d; /* Dark grey for demo */
}

.demo-btn:hover {
  background-color: #3c3c3c;
}

.repo-btn {
  background-color: #333; /* GitHub-like color */
}

.repo-btn:hover {
  background-color: #252525;
}

.btn-icon {
  font-size: 24px; /* Adjust the icon size */
  transition: transform 0.3s ease;
}

.btn:hover .btn-icon {
  transform: scale(1.1);
}

@media (max-width: 768px) {
  .buttons {
    flex-direction: column;
    gap: 15px;
  }
}

/* Dark Mode */
.dark-mode .projects,
.dark-mode .projects h2 {
  color: #f0f0f0;
}

.dark-mode .project-card {
  background-color: #2c2c3e;
}

.dark-mode .image-wrapper {
  background-color: #3c3c4e;
}

.dark-mode .card-content h3,
.dark-mode .card-content p,
.dark-mode .tech-badge {
  color: #f0f0f0;
}

.dark-mode .tech-badge {
  background-color: #444;
}
</style>
